import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getOverview, type Overview } from '@/api/admin';

const POLL_MS = 5_000;
const STALE_MS = 15_000;
export const overviewPollInterval = () => document.visibilityState === 'visible' ? POLL_MS : false;

export default function OverviewPage() {
  const queryClient = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: ({ signal }) => getOverview(signal),
    staleTime: STALE_MS,
    refetchInterval: overviewPollInterval,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [queryClient]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  if (overview.isPending) return <p role="status">Loading operational overview…</p>;
  if (!overview.data) return <section className="notice error" role="alert"><strong>Overview unavailable</strong><span>{overview.error instanceof Error ? overview.error.message : 'Unknown error'}</span></section>;

  const data = overview.data;
  const puzzleReporting = data.productActivity.puzzleReporting
    ?? { status: 'unavailable' as const, detail: 'the backend did not return the authoritative puzzle report' };
  /*
    The derived sections added in B6, B7 and C3 report `{ status: 'unavailable' }`
    when their query failed and carry no `status` at all when they succeeded, so
    the presence of that key IS the discriminator. `null` here means "no figures",
    which every tile below renders as a dash rather than as a zero.
  */
  const games = available(data.productActivity.onlineGameReporting);
  const analysis = available(data.productActivity.analysisReporting);
  const training = available(data.productActivity.trainingReporting);
  const stale = overview.isError || now - overview.dataUpdatedAt > STALE_MS;
  return <>
    <div className="page-title"><div><p className="eyebrow">Founder operations</p><h1>Production overview</h1></div><div className={`freshness ${stale ? 'stale' : 'live'}`} role="status">{stale ? 'Stale data' : 'Live'} · received {formatTime(overview.dataUpdatedAt)}</div></div>
    {overview.isError && <section className="notice warning" role="alert">Latest refresh failed. Showing the last successful snapshot.</section>}

    <MeasurementBanner measurement={data.measurement} />

    <Section title="Production"><MetricGrid>
      <Metric label="API status" value={data.system.dependencies.database.status === 'down' ? 'Degraded' : 'Operational'} />
      <Metric label="Running SHA" value={data.version.gitSha.slice(0, 10)} mono />
      <Metric label="Built" value={formatDate(data.version.builtAt)} />
      <Metric label="Process uptime" value={duration(data.system.processUptimeSeconds)} />
    </MetricGrid></Section>

    <ProductHistory productActivity={data.productActivity} />

    <TrafficFilter
      traffic={data.traffic}
      ignored={data.productActivity.botHeartbeatsIgnored}
      day={data.productActivity.day}
    />

    <Section title="Product activity today" description={`${data.productActivity.day} · ${data.productActivity.timeZone} · durable daily totals that do not reset when the backend is deployed.`}><MetricGrid>
      <Metric label="Positions served" value={puzzleReporting.status === 'ok' ? puzzleReporting.served : '—'} />
      <Metric label="Tries started" value={puzzleReporting.status === 'ok' ? puzzleReporting.eventFlow.started : '—'} />
      <Metric label="Results recorded" value={puzzleReporting.status === 'ok' ? puzzleReporting.eventFlow.results : '—'} />
      <Metric label="Solved" value={puzzleReporting.status === 'ok' ? puzzleReporting.eventFlow.solved : '—'} />
      <Metric label="Failed" value={puzzleReporting.status === 'ok' ? puzzleReporting.eventFlow.failed : '—'} />
      {/* D1 — the cohort's BOTH halves, so the reconciliation is visible rather
          than asserted. Resolved plus unresolved is the started figure above. */}
      <Metric label="Cohort · resolved" value={puzzleReporting.status === 'ok' ? puzzleReporting.startCohort.resolvedByWatermark : '—'} />
      <Metric label="Cohort · unresolved" value={puzzleReporting.status === 'ok' ? puzzleReporting.startCohort.unresolvedAtWatermark : '—'} />
      <Metric label="Runs started" value={training ? training.runs.started : '—'} />
      <Metric label="Scoring people" value={puzzleReporting.status === 'ok' ? puzzleReporting.scoringIdentities : '—'} />
      {/* D1 — THE DENOMINATOR, SPLIT. One number for "people" hides that most of
          it is browsers we cannot prove are people. */}
      <Metric label="· signed in" value={puzzleReporting.status === 'ok' ? puzzleReporting.authenticatedUsers : '—'} />
      <Metric label="· anonymous browsers" value={puzzleReporting.status === 'ok' ? puzzleReporting.anonymousBrowserIdentities : '—'} />
      <Metric label="Results / person · median" value={puzzleReporting.status === 'ok' ? decimal(puzzleReporting.scoredResultsPerIdentity.median) : '—'} />
      <Metric label="Results / person · mean" value={puzzleReporting.status === 'ok' ? decimal(puzzleReporting.scoredResultsPerIdentity.mean) : '—'} />
      <Metric label="Results / person · p90" value={puzzleReporting.status === 'ok' ? decimal(puzzleReporting.scoredResultsPerIdentity.p90) : '—'} />
      <Metric label="Results / person · maximum" value={puzzleReporting.status === 'ok' ? decimal(puzzleReporting.scoredResultsPerIdentity.maximum) : '—'} />
      <Metric label="Top solver's share" value={puzzleReporting.status === 'ok' ? share(puzzleReporting.scoredResultsPerIdentity.topSolverShare) : '—'} />
      {/* B5 — one request can return several positions, so both numbers are
          shown. Their ratio is the batch size and is stated rather than left to
          be worked out. */}
      <Metric label="Serve requests" value={puzzleReporting.status === 'ok' ? puzzleReporting.serving.requests : '—'} />
      <Metric label="Positions / request" value={puzzleReporting.status === 'ok' ? decimal(puzzleReporting.serving.positionsPerRequest.mean) : '—'} />
      {/* D2 — derived sources, not the shadow counters these tiles used to read. */}
      <Metric label="Analysis requests" value={analysis ? analysis.requests : '—'} />
      <Metric label="Analyses repeated" value={analysis ? analysis.repeatRequests : '—'} />
      <Metric label="Games started" value={games ? games.started : '—'} />
      <Metric label="Games completed" value={games ? games.completed : '—'} />
    </MetricGrid>
    <p className="footnote">
      {puzzleReporting.status === 'ok'
        ? <>Window <strong>{puzzleReporting.windowStart}</strong> to <strong>{puzzleReporting.asOfExclusive}</strong> UTC (exclusive). Authenticated accounts and durable anonymous browser identities are each counted once across all sessions. {puzzleReporting.legacyUnkeyed} legacy events lack a presentation ID; {puzzleReporting.excludedInternal} classified internal events are excluded.</>
        : <>Authoritative puzzle reporting is unavailable: {puzzleReporting.detail}</>}
    </p>
    <p className="footnote">
      <strong>Median first, mean second</strong> — the mean is pulled upward by one heavy
      solver, so it is not the typical player. Read the top solver's share beside it: a high
      figure means most of today's activity is one person. <strong>Event-flow</strong> results
      can belong to starts from yesterday, so results and starts need not match; the{' '}
      <strong>cohort</strong> selects today's starts alone and its two halves always add up to
      Tries started.
    </p>
    <p className="footnote">
      <strong>Anonymous browsers are not people.</strong> Each is one browser that kept its
      identity; the same person on a phone and a laptop is two, and we do not stitch them
      together because we cannot prove it. And when a guest signs in, their earlier activity is
      re-counted under the account — so a past day's denominator can go DOWN after the fact.
      Today's row covers this reporting day only up to, and not including, the watermark above.
    </p></Section>

    {/*
      D2 — A GAUGE IS NOT A DAILY TOTAL, so it does not share a grid with one.
      These three read the queue as it stands at the instant of the request; the
      section above counts events over a reporting window. Putting them side by
      side invited the arithmetic nobody should do.
    */}
    <Section
      title="Live gauges"
      description={`Read now, not accumulated — ${formatTime(Date.parse(data.productActivity.explanationQueue.observedAt))}. These are current depths and counts, and none of them belongs in a daily total.`}
    ><MetricGrid>
      <Metric label="Explanations queued" value={data.productActivity.explanationQueue.queued} />
      <Metric label="Explanations in flight" value={data.productActivity.explanationQueue.inFlight} />
      <Metric label="Explanations baked" value={data.productActivity.explanationQueue.baked} />
      <Metric label="Games in progress" value={games ? games.activeNow : '—'} />
      <Metric label="Runs in progress" value={training ? training.runs.activeNow : '—'} />
      <Metric label="Training plans active" value={training ? training.plans.activeNow : '—'} />
      <Metric label="Analyses running" value={analysis ? analysis.stillRunning : '—'} />
    </MetricGrid>
    <p className="footnote">
      <strong>Runs in progress</strong> includes runs nobody will come back to. A run is only
      marked abandoned when the player stops it deliberately; one walked away from stays active
      for ever, because nothing sweeps it. So this is not a measure of engagement, and{' '}
      <strong>runs abandoned</strong> below is not a measure of giving up.
      <strong> Analyses running</strong> counts requests accepted and never settled, which
      includes any lost to a restart.
    </p></Section>

    {/*
      D2 — OUR OWN TRAFFIC, ON ITS OWN. These rows are real activity and are
      excluded from every customer figure above; showing them nowhere would make
      the exclusion unauditable, and showing them in the same grid would put
      probe runs back into the numbers this plan exists to clean.
    */}
    <Section
      title="Internal and probe activity today"
      description="Classified as ours and excluded from every customer figure above. Kept visible so the exclusion can be checked rather than trusted."
    ><MetricGrid>
      <Metric label="Internal events excluded" value={puzzleReporting.status === 'ok' ? puzzleReporting.excludedInternal : '—'} />
      <Metric label="Events with no presentation ID" value={puzzleReporting.status === 'ok' ? puzzleReporting.legacyUnkeyed : '—'} />
      <Metric label="Games with no recorded start" value={games ? games.startedAtUnknown : '—'} />
    </MetricGrid>
    <p className="footnote">
      <strong>Internal events</strong> are requests that presented a valid operator key. A
      request that did not is counted as a customer, by design: a public client must never be
      able to declare itself internal.
      <strong> No presentation ID</strong> is pre-2026-08-18 activity, which cannot be
      classified either way and is counted with customers rather than dropped.
      <strong> No recorded start</strong> is games saved before migration 026, when only the end
      time was stored. Crawler heartbeats are counted separately, under Traffic filter.
    </p></Section>

    <Section
      title="Growth"
      description="Durable counts from the database and Cognito — NOT since restart, unlike Product activity above. Each figure names its own period."
    ><MetricGrid>
      <Metric label="Accounts · today" value={data.productActivity.accountsCreatedToday ?? '—'} />
      <Metric label="Accounts · all time" value={data.productActivity.accountsCreated ?? '—'} />
      <Metric label="Training plans · today" value={data.productActivity.trainingPlansToday ?? '—'} />
      <Metric label="Training plans · all time" value={data.productActivity.trainingPlansCreated ?? '—'} />
      <Metric label="Plans by someone who trained" value={data.productActivity.trainingPlansWithActivity ?? '—'} />
      <Metric label="Runs completed today" value={training ? training.runs.completed : '—'} />
      <Metric label="Runs abandoned today" value={training ? training.runs.abandoned : '—'} />
    </MetricGrid>
    <p className="footnote">
      {/* C4 — Cognito is a paginated external walk with no transaction, so it
          reports when IT was read rather than borrowing the window's watermark. */}
      <strong>Accounts</strong> come from Cognito, read separately at{' '}
      {data.productActivity.accountsObservedAt
        ? formatTime(Date.parse(data.productActivity.accountsObservedAt))
        : 'an unknown time'}
      {data.productActivity.accountsStatus === 'ok' ? '' : ` (${data.productActivity.accountsStatus})`}
      {' '}— not at the same instant as the database figures, because it is paged and cannot be
      read atomically with them. It excludes records left unconfirmed — they cannot sign in.
      <strong> Runs completed</strong> and <strong>abandoned</strong> are counted by when they
      ENDED, so they need not match runs started today: a run can begin one day and finish the
      next, and forcing them to balance would move an event to a day it did not happen on.
      Compare <strong>plans by someone who trained</strong> against the all-time total: on
      2026-08-08, 74 of 83 plans belonged to identities that had never attempted a single
      puzzle, because production probes create real plans and each browser context mints a
      fresh identity. A large gap means the total is mostly test traffic, not players.
    </p></Section>

    <Section title="System"><MetricGrid>
      <Metric label="Database" value={data.system.dependencies.database.status} />
      <Metric label="Database latency" value={`${data.system.dependencies.database.responseTimeMs ?? '—'} ms`} />
      <Metric label="Heap used" value={bytes(data.system.memoryBytes.heapUsed)} />
      <Metric label="RSS memory" value={bytes(data.system.memoryBytes.rss)} />
      <Metric label="Cache" value={data.system.dependencies.cache.status} />
      <Metric label="Node.js" value={data.system.nodeVersion} />
    </MetricGrid></Section>
  </>;
}

/**
 * Daily product history.
 *
 * Until 2026-08-08 every row not matching today was deleted on the first
 * heartbeat after midnight, so two days could never be compared and no trend
 * existed at all.
 */
function ProductHistory({ productActivity }: { productActivity: Overview['productActivity'] }) {
  // The admin app and the API deploy separately, so a new page can meet an older
  // backend. A missing panel beats a crash that takes the dashboard with it.
  const history = productActivity.history ?? [];
  if (!history.length) {
    return <Section title="Product history" description="Unavailable — no daily history was returned.">
      <p className="key-values"><span>No history</span></p>
    </Section>;
  }
  /*
    The admin app and the API deploy separately, so for a few minutes after
    either one ships this page can meet the OTHER shape of this array. Rendering
    it anyway gives a table of blank cells and a NaN bar width, which reads as a
    dead panel rather than as a version skew. Checked on a field the old shape
    never had.
  */
  if (typeof history[0].served !== 'number') {
    return <Section
      title="Product history"
      description="Unavailable — the API is returning the previous shape of this table. It will right itself when both sides are on the same build."
    ><p className="key-values"><span>Waiting for the API</span></p></Section>;
  }

  const peak = Math.max(1, ...history.map(entry => entry.results));

  return <Section
    title="Product history"
    description={`${history.length} days, back to ${history[0].day} · the same figures as Product activity today, from the same tables · today is still accruing, so it is not comparable with the whole days beside it.`}
  >
    <table className="history-table">
      <caption className="sr-only">Positions served, tries, results and people per day</caption>
      <thead><tr>
        <th scope="col">Day</th>
        <th scope="col">Served</th>
        <th scope="col">Tries started</th>
        <th scope="col">Results</th>
        <th scope="col">Solved</th>
        <th scope="col">Failed</th>
        <th scope="col">People</th>
        <th scope="col"><span className="sr-only">Relative results recorded</span></th>
      </tr></thead>
      <tbody>
        {[...history].reverse().map(entry => {
          const isToday = entry.day === history[history.length - 1].day;
          return <tr key={entry.day} className={isToday ? 'is-today' : undefined}>
            <th scope="row">{entry.day}{isToday && <span className="tag"> so far</span>}</th>
            <td>{entry.served}</td>
            <td>{entry.started}</td>
            <td>{entry.results}</td>
            <td>{entry.solved}</td>
            <td>{entry.failed}</td>
            <td>{entry.people}</td>
            <td>
              <span
                className="bar"
                style={{ width: `${Math.round((entry.results / peak) * 100)}%` }}
                aria-hidden="true"
              />
            </td>
          </tr>;
        })}
      </tbody>
    </table>
    <p className="footnote">
      <strong>Today's row is the tiles above, not a second opinion.</strong> Every column comes
      from the tables the reporting contract reads, through the same rules: internal and probe
      traffic excluded, pre-2026-08-18 activity counted with customers because it cannot be
      classified either way, and the same Europe/Bucharest day boundary.
      <strong> People</strong> counts distinct identities that recorded a result, so one person
      solving thirty puzzles is one. It is not a visitor count.
    </p>
    <p className="footnote">
      <strong>The table starts where the data does.</strong> `puzzle_presentations` began on
      2026-08-18, so there is no earlier day to report: a row before it would show 0 served
      against real play, which is a zero meaning "not measured". The window opens out on its
      own as the days accrue, up to a fortnight.
    </p>
  </Section>;
}

/**
 * What the crawler filter is doing.
 *
 * Exists so the filter can be AUDITED rather than trusted. On 2026-08-08 the
 * dashboard reported 139 visitors for a day in which 108 of them were on
 * /champions/* with zero active seconds — crawlers, which do not persist the
 * localStorage visitor id and so mint a new "visitor" on every page they fetch.
 * Filtering them fixes the count; this panel is how you check it did not also
 * remove real people, which is the failure that would otherwise be invisible.
 */
function TrafficFilter({ traffic, ignored, day }: {
  traffic: Overview['traffic'];
  /* Nullable since C6: `null` is "the counter could not be read", which the
     `?? '—'` below already renders correctly and used to never receive. */
  ignored: number | null;
  day: string;
}) {
  const agents = traffic?.topUserAgents ?? [];
  return <Section
    title="Traffic filter"
    description={`Crawler heartbeats are answered and discarded, not counted. Aggregate tally for ${day} — no visitor is identified here.`}
  >
    <MetricGrid>
      <Metric label="Crawler heartbeats ignored" value={ignored ?? '—'} />
      <Metric label="Distinct agents seen" value={agents.length || '—'} />
      <Metric label="Classified as crawler" value={agents.filter(a => a.isBot).length} />
      <Metric label="Classified as browser" value={agents.filter(a => !a.isBot).length} />
    </MetricGrid>

    {agents.length === 0
      ? <p className="footnote">No heartbeats seen since the last restart.</p>
      : <table className="history-table">
        <caption className="sr-only">User agents seen on the presence heartbeat</caption>
        <thead><tr>
          <th scope="col">User agent</th>
          <th scope="col">Heartbeats</th>
          <th scope="col">Verdict</th>
        </tr></thead>
        <tbody>
          {agents.map(agent => <tr key={agent.agent}>
            <th scope="row" className="agent">{agent.agent}</th>
            <td>{agent.count}</td>
            <td>{agent.isBot ? `crawler · ${agent.reason}` : 'counted'}</td>
          </tr>)}
        </tbody>
      </table>}

    <p className="footnote">
      Read the <strong>counted</strong> rows, not the crawler ones: a missed crawler hides
      there, and a real browser wrongly marked as a crawler is a person silently deleted from
      every figure on this page.
    </p>
  </Section>;
}

function previousDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, date) - 86_400_000);
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Does the site still measure anything?
 *
 * First on the page because a blind site invalidates every figure below it. On
 * 2026-08-08 a CI build shipped with no GA4 measurement id, every event was
 * discarded for a day, and this dashboard reported healthy traffic throughout —
 * the failure was found from a Google Ads number instead. Everything else here
 * measures the product; this measures the measuring.
 */
function MeasurementBanner({ measurement }: { measurement: Overview['measurement'] }) {
  // Tolerate an older API: the admin app and the backend deploy separately.
  if (!measurement) return null;

  if (measurement.status === 'missing') {
    return <section className="notice error" role="alert">
      <strong>Analytics is blind — the served bundle has no measurement id</strong>
      <span>
        Every GA4 event is being discarded, so conversions cannot record. Checked{' '}
        {measurement.bundle ?? 'the site'}
        {measurement.checkedAt ? ` at ${formatTime(Date.parse(measurement.checkedAt))}` : ''}.
      </span>
    </section>;
  }

  if (measurement.status === 'unreachable') {
    return <section className="notice warning" role="status">
      <strong>Could not check analytics</strong>
      <span>{measurement.detail ?? 'the public site could not be read'} — this is not the same as analytics being broken.</span>
    </section>;
  }

  return <section className="panel"><div className="section-heading"><h2>Measurement</h2>
    <p>The served bundle still carries a measurement id, so events can reach GA4. Checked against the public site, not the repository.</p>
  </div><MetricGrid>
    <Metric label="Analytics" value="Reporting" />
    <Metric label="Measurement ID" value={measurement.measurementId ?? '—'} mono />
    <Metric label="Bundle checked" value={measurement.bundle?.replace('/assets/', '') ?? '—'} mono />
    <Metric label="Last checked" value={measurement.checkedAt ? formatTime(Date.parse(measurement.checkedAt)) : '—'} />
  </MetricGrid></section>;
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) { return <section className="panel"><div className="section-heading"><h2>{title}</h2>{description && <p>{description}</p>}</div>{children}</section>; }
function MetricGrid({ children }: { children: React.ReactNode }) { return <div className="metric-grid">{children}</div>; }
function Metric({ label, value, mono = false }: { label: string; value: string | number; mono?: boolean }) { return <div className="metric"><span>{label}</span><strong className={mono ? 'mono' : undefined}>{value}</strong></div>; }
function KeyValues({ values, empty }: { values: Record<string, number>; empty: string }) { const active = Object.entries(values).filter(([, value]) => value > 0); return <div className="key-values">{active.length ? active.map(([key, value]) => <span key={key}>{key.replace('-', ' ')} <strong>{value}</strong></span>) : <span>{empty}</span>}</div>; }
const formatTime = (time: number) => new Date(time).toLocaleTimeString();
const formatDate = (value: string) => new Date(value).toLocaleString();
const duration = (seconds: number) => `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
/**
 * A derived reporting section, or `null` when its source could not be read.
 *
 * The union is discriminated by the PRESENCE of `status`, not by its value: a
 * healthy section is the figures alone. Returning `null` rather than a zeroed
 * object is the whole point — the page renders a dash, and a failed read never
 * gets to look like a quiet day.
 */
function available<T extends object>(
  section: T | { status: 'unavailable'; detail: string } | undefined,
): T | null {
  if (!section) return null;
  return 'status' in section ? null : section;
}

const bytes = (value: number) => `${Math.round(value / 1024 / 1024)} MB`;
const decimal = (value: number | null) => value == null ? '—' : value.toFixed(1);
const share = (value: number | null) => value == null ? '—' : `${Math.round(value * 100)}%`;
